import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Mail } from 'lucide-react';
import AuthBrandLogo from '../../branding/AuthBrandLogo';
import { identityApi } from '../../api/client';
import { ROUTES } from '../../routes/constants';

function AuthShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-base px-4 relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[700px] h-[500px] rounded-full bg-brand/[0.06] blur-[120px]" />
        <div className="absolute -bottom-32 -right-32 w-[500px] h-[500px] rounded-full bg-info/[0.05] blur-[100px]" />
        <div className="absolute top-1/2 -left-32 w-[350px] h-[350px] rounded-full bg-bull/[0.03] blur-[80px]" />
      </div>
      <div className="w-full max-w-sm relative z-10">{children}</div>
    </div>
  );
}

export default function VerifyEmailPage() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();

  const urlToken   = searchParams.get('token') || '';
  const [token, setToken]     = useState(urlToken);
  const [tokenLoaded]         = useState(!!urlToken);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) { setError('Please enter your verification token.'); return; }
    setError('');
    setLoading(true);
    try {
      await identityApi.verifyEmail(token.trim());
      setSuccess(true);
      setTimeout(() => navigate(ROUTES.LOGIN, { replace: true }), 2500);
    } catch (err: any) {
      setError(err?.message || 'Verification failed. The token may be expired or invalid.');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <AuthShell>
        <div className="text-center space-y-4">
          <CheckCircle className="w-12 h-12 text-bull mx-auto" />
          <h2 className="font-semibold text-txt-primary">Email verified!</h2>
          <p className="text-xs text-txt-muted">Redirecting you to sign in…</p>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <div className="text-center mb-8">
        <AuthBrandLogo />
        <div className="flex items-center justify-center gap-2 mt-3">
          <Mail size={13} className="text-brand" />
          <p className="text-2xs font-mono text-txt-muted tracking-widest uppercase">
            Verify your email address
          </p>
        </div>
      </div>

      <form
        onSubmit={handleSubmit}
        className="relative bg-bg-panel border border-bg-border/60 rounded-xl p-6 space-y-5 shadow-modal overflow-hidden"
      >
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-brand/50 to-transparent" />

        {tokenLoaded ? (
          <div className="flex items-center gap-2 text-xs text-bull bg-bull/10 border border-bull/30 rounded-lg px-3 py-2">
            <CheckCircle size={13} />
            <span>Token loaded — click Verify Email to confirm.</span>
          </div>
        ) : (
          <div className="space-y-1.5">
            <label className="text-2xs font-mono text-txt-label uppercase tracking-widest">
              Verification Token
            </label>
            <input
              type="text"
              value={token}
              onChange={(e) => setToken(e.target.value)}
              placeholder="Paste your token here"
              className="input-base font-mono text-xs"
              disabled={loading}
            />
          </div>
        )}

        {error && (
          <p className="text-xs text-bear bg-bear/10 border border-bear/30 rounded-lg px-3 py-2">{error}</p>
        )}

        <button type="submit" disabled={loading} className="btn-primary w-full justify-center mt-1">
          {loading ? 'Verifying…' : 'Verify Email'}
        </button>

        <div className="border-t border-bg-border/40 pt-3 text-center">
          <Link to={ROUTES.LOGIN} className="text-xs text-txt-muted hover:text-brand transition-colors">
            Back to sign in
          </Link>
        </div>
      </form>
    </AuthShell>
  );
}
